import { QUEST_ITEMS_PER_PAGE } from 'config/constants/vvsgotchi'
import { useTranslation } from 'contexts/Localization'
import useToast from 'hooks/useToast'
import { differenceWith, isEqual } from 'lodash'
import { useEffect } from 'react'
import { usePrevious } from 'react-use'
import { formatNumber } from 'utils/formatBalance'
import { useApolloClient, useQuestHistoryQuery, QuestHistoryDocument } from 'vvs-gotchi-graphql'
import { QuestSymbol, questSymbolFromGraphqlDataMap } from 'state/vvsgotchi/models/quest'
import PixelatedImage from 'views/VvsGotchi/components/PixelatedImage'
import { toastTypes } from 'components/Toast'
import getQuestConfigs from '../questConfigs'
import { useVvsGotchiAudioModeManager } from './useConfigManager'
import sound from '../../../assets/quest_success.mp3'

const IGNORED_QUEST: QuestSymbol[] = [QuestSymbol.GACHA]
const questSuccessSound = new Audio(sound)

/**
 * Notify quest completed by comparing Quest History
 * @param address Connected wallet address
 */
export const useNotifyQuestCompleted = (address: string) => {
  const { toastVVSGotchi } = useToast()
  const { t } = useTranslation()
  const { sePlay } = useVvsGotchiAudioModeManager()

  const client = useApolloClient()
  const data = client?.readQuery<ReturnType<typeof useQuestHistoryQuery>['data']>({
    query: QuestHistoryDocument,
    variables: {
      address,
      first: QUEST_ITEMS_PER_PAGE,
    },
  })
  const questHistory = data?.owner?.questHistory?.questHistoryConnection?.edges.map(({ node }) => ({
    completedAt: node.completedAt,
    symbol: node.symbol,
    earnedExp: node.earnedExp,
  }))
  const prevQuestHistory = usePrevious(questHistory)

  useEffect(() => {
    const diff =
      questHistory?.length > 0 && prevQuestHistory?.length > 0
        ? differenceWith(
            questHistory?.slice(0, QUEST_ITEMS_PER_PAGE),
            prevQuestHistory?.slice(0, QUEST_ITEMS_PER_PAGE),
            isEqual,
          )
        : []
    if (diff.length > 0) {
      diff
        .filter((q) => !IGNORED_QUEST.includes(questSymbolFromGraphqlDataMap[q.symbol]))
        .forEach((c) => {
          if (sePlay) {
            questSuccessSound.play()
          }
          toastVVSGotchi(
            t('Quest Completed: %questName%', { questName: getQuestConfigs(t)[c.symbol].name }),
            toastTypes.INFO,
            <PixelatedImage src="/images/vvsGotchi/tier/master.png" width="32px" />,
            t('Earned %exp% Exp', { exp: formatNumber(c.earnedExp, 0, 3) }),
          )
        })
    }
  }, [questHistory, prevQuestHistory, toastVVSGotchi, t, sePlay])
}
